import { Button, cn, EmptyState, IconTile, Skeleton } from "@love-cpmd/ui";
import { CloudOff, RefreshCw, Share, X } from "lucide-react";
import type { ReactNode } from "react";
import logoUrl from "../../assets/love-cpmd-logo.webp";
import { useLocalState, useOnline } from "../../lib/hooks";

export function Logo({ className, withText = true, subtitle = "Knowledge Sharing" }: { className?: string; withText?: boolean; subtitle?: string }) {
  return (
    <div className={cn("flex min-w-0 items-center gap-2.5", className)}>
      <LogoMark className="size-10 shrink-0" decorative={withText} />
      {withText && (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] leading-none font-extrabold tracking-tight text-primary">LOVE CPMD</span>
          <span className="mt-1 truncate text-label-sm text-on-surface-variant">{subtitle}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The LOVE CPMD heart: "C" and "P" folded from one ribbon. Transparent background, so it sits on
 * light surfaces and on the dark login backdrop alike. `decorative` when a visible name or an
 * aria-label already says "LOVE CPMD".
 */
export function LogoMark({ className, decorative = false }: { className?: string; decorative?: boolean }) {
  return (
    <img
      src={logoUrl}
      alt={decorative ? "" : "LOVE CPMD"}
      width={256}
      height={256}
      decoding="async"
      draggable={false}
      className={cn("object-contain select-none", className)}
    />
  );
}

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ eyebrow, title, description, actions, className }: PageHeaderProps) {
  return (
    <header className={cn("flex flex-col gap-4 md:flex-row md:items-end md:justify-between", className)}>
      <div className="flex min-w-0 max-w-3xl flex-col gap-1.5">
        {eyebrow && <div className="flex flex-wrap items-center gap-2 text-label-sm font-bold tracking-wider text-primary uppercase">{eyebrow}</div>}
        <h1 className="text-headline-lg text-on-surface md:text-headline-xl">{title}</h1>
        {description && <p className="text-body-md text-on-surface-variant md:text-body-lg">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function PageContainer({ children, className, width = "default" }: { children: ReactNode; className?: string; width?: "default" | "narrow" | "wide" }) {
  return (
    <div
      className={cn(
        "mx-auto flex w-full flex-col gap-6 px-4 pt-5 pb-28 md:px-6 md:pt-6 md:pb-12 lg:gap-7 lg:px-8",
        width === "narrow" ? "max-w-4xl" : width === "wide" ? "max-w-[1400px]" : "max-w-[1280px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" className="flex items-center justify-center gap-2 bg-inverse-surface px-4 py-2 text-center text-label-md text-inverse-on-surface">
      <CloudOff className="size-4 shrink-0" aria-hidden />
      You're offline. You can still read the latest data; changes are paused until you reconnect.
    </div>
  );
}

export function LoadingCards({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("grid gap-4", className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-36 rounded-card" />
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <PageContainer>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-9 w-72" />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Skeleton className="h-64 rounded-card lg:col-span-2" />
        <div className="flex flex-col gap-5">
          <Skeleton className="h-[118px] rounded-card" />
          <Skeleton className="h-[118px] rounded-card" />
        </div>
      </div>
      <Skeleton className="h-48 rounded-card" />
    </PageContainer>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <EmptyState
      icon={<RefreshCw />}
      title="We couldn't load this page"
      description="Check your connection and try again."
      action={
        onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )
      }
    />
  );
}

/** Safari iOS: one-time hint to add the PWA to the home screen (plan: PWA). */
export function InstallHint() {
  const [dismissed, setDismissed] = useLocalState("love-cpmd:install-hint", false);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches;
  if (dismissed || !isIos || standalone) return null;
  return (
    <div className="flex items-start gap-3 rounded-card border border-white/70 bg-surface-container-lowest p-4 shadow-elevated">
      <IconTile size="sm">
        <Share />
      </IconTile>
      <p className="flex-1 text-body-md text-on-surface">
        Install LOVE CPMD: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
      </p>
      <button type="button" aria-label="Dismiss" onClick={() => setDismissed(true)} className="rounded-lg p-1 text-on-surface-variant hover:bg-surface-container">
        <X className="size-4" />
      </button>
    </div>
  );
}
